import { IUser, _dataProvider } from '../dataProvider/DataProvider';
import { useRef, useState } from 'react';

import { Callout, DefaultButton, DirectionalHint } from '@fluentui/react';
import React from 'react';
import { SERVER_URL } from '../SharedCommon/utils';
import allUserAvatar from '../img/icon-All.png';
import defaultAvatar from '../img/icon-DefaultProfilePicture.png';
import { getUserAvatarColor } from '../utils/avatar';
import i18n from '../i18n';
import plusAvatar from '../img/icon-Plus.png';
import plusIcon from '../img/icon-plus-hover-over-state.png';

// icon-friend.svg 的路径数据
const iconFriendPath =
  'M379.457362 489.951319a262.557957 262.557957 0 0 0 393.847829-227.382468C773.316085 117.553021 655.763064 0 510.747234 0 365.731404 0 248.189277 117.553021 248.189277 262.557957c0 93.794043 50.034383 180.485447 131.268085 227.393362z m131.289872 61.44C249.725277 551.380426 38.12766 762.967149 38.12766 1024h945.239149c0-261.011064-211.608511-472.619574-472.619575-472.619574z';

const prebuiltAvatars = [plusAvatar, allUserAvatar];

// 默认头像 SVG 组件 - 支持动态修改颜色
type DefaultAvatarSvgProps = {
  iconColor: string;
  backgroundColor: string;
  className?: string;
};

const DefaultAvatarSvg: React.FC<DefaultAvatarSvgProps> = ({ iconColor, backgroundColor, className }) => {
  // 计算居中偏移：缩小到 0.56 倍，剩余空间为 1024 * (1 - 0.56) = 450.56，每边留 225.28
  const offset = (1024 * (1 - 0.56)) / 2;
  return (
    <svg
      viewBox='0 0 1024 1024'
      version='1.1'
      xmlns='http://www.w3.org/2000/svg'
      width='100%'
      height='100%'
      className={className}
      style={{ display: 'block' }}>
      <rect width='1024' height='1024' fill={backgroundColor} rx='512' />
      <g transform={`translate(${offset}, ${offset}) scale(0.56)`}>
        <path d={iconFriendPath} fill={iconColor} />
      </g>
    </svg>
  );
};

export const AvatarImage = (props: any) => {
  const { userId } = props;
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const className = props.className ?? 'avatar-img';

  // 计算头像 URL
  let imageUrl: string | null = null;
  if (userId < 0) {
    const idx = 0 - userId - 1;
    if (idx < 2) {
      imageUrl = prebuiltAvatars[idx];
    }
  } else {
    imageUrl = _dataProvider.getAvatarSync(userId);
  }

  const handleLoad = () => {
    setImageLoaded(true);
  };

  const handleError = () => {
    setImageLoaded(false);
  };

  // 获取默认 SVG 头像的颜色
  const getDefaultAvatar = () => {
    if (userId > 0) {
      const { avatarIconColor, avatarBackgroundColor } = getUserAvatarColor(userId);
      return (
        <DefaultAvatarSvg iconColor={avatarIconColor} backgroundColor={avatarBackgroundColor} className={className} />
      );
    }
    const { avatarIconColor, avatarBackgroundColor } = getUserAvatarColor(0);
    return (
      <DefaultAvatarSvg iconColor={avatarIconColor} backgroundColor={avatarBackgroundColor} className={className} />
    );
  };

  // 如果没有有效 URL 或者是默认头像，直接显示 SVG
  if (!imageUrl || (userId > 0 && imageUrl === defaultAvatar)) {
    return getDefaultAvatar();
  }

  // 如果有有效 URL，先显示 SVG，同时加载图片
  return (
    <>
      {/* 默认 SVG 头像，作为背景 */}
      {!imageLoaded && getDefaultAvatar()}
      <img
        style={{ display: imageLoaded ? 'inherit' : 'none' }}
        src={imageUrl}
        alt=''
        className={className}
        onLoad={handleLoad}
        onError={handleError}
      />
    </>
  );
};

export const GroupImage = (props: any) => {
  const { item, className } = props;

  // 一对一，使用好友头像
  if (item.isOneOnOneGroup) {
    return <AvatarImage userId={item.friendUserId} className={className} />;
  }

  // 使用后端返回的组合头像
  const avatarUrl = `${SERVER_URL}/group/avatar/${item.avatar}`;
  return <img src={avatarUrl} className={className} alt='Group Avatar' />;
};

const wrapNameStyle: React.CSSProperties = {
  maxWidth: '100%',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  textOverflow: 'clip'
};

type AvatarItemProps = {
  userId: number;
  name?: string;
  size: string;
  fontSize: number;
  nameWidth: string;
  wrapName: boolean;
};

// 名字被省略号截断时，鼠标悬停在头像或名字上，在头像正下方（覆盖截断的名字）显示完整名字
const AvatarItem: React.FC<AvatarItemProps> = ({ userId, name, size, fontSize, nameWidth, wrapName }) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const [showTip, setShowTip] = useState(false);

  const handleMouseEnter = () => {
    const el = nameRef.current;
    if (el && el.scrollWidth > el.clientWidth) {
      setShowTip(true);
    }
  };

  return (
    <div
      className='avatar-item'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTip(false)}
      onClick={() => setShowTip(false)}>
      <div ref={imageRef} className='avatar-container' style={{ width: size, height: size }}>
        <AvatarImage userId={userId} />
      </div>
      {name !== undefined && (
        <div
          ref={nameRef}
          className='avatar-name'
          style={wrapName ? { fontSize, ...wrapNameStyle } : { fontSize, width: nameWidth }}>
          {name}
        </div>
      )}
      {showTip && (
        <Callout
          target={imageRef}
          role='tooltip'
          directionalHint={DirectionalHint.bottomCenter}
          isBeakVisible={false}
          gapSpace={0}
          setInitialFocus={false}
          onDismiss={() => setShowTip(false)}
          // 不拦截鼠标，否则覆盖在名字上时会触发 mouseleave 导致闪烁
          layerProps={{ styles: { root: { pointerEvents: 'none' } } }}
          styles={{
            root: { pointerEvents: 'none !important' },
            calloutMain: { padding: '4px 8px', fontSize, whiteSpace: 'nowrap' }
          }}>
          {name}
        </Callout>
      )}
    </div>
  );
};

export const userAvatarName = (
  user: Omit<IUser, 'isGroupLeader'>,
  showName = 1,
  showSetting = false,
  size = '60px',
  fontSize = 14,
  nameWidth?: string,
  wrapName = false
) => {
  const fullname = user.displayName ?? user.name;
  const name = showName == 1 ? fullname : fullname + ' ' + (showName === 3 ? user.tag ?? '' : '');

  return (
    <>
      <AvatarItem
        userId={user.id || user.userId}
        name={showName ? name : undefined}
        size={size}
        fontSize={fontSize}
        nameWidth={nameWidth ?? size}
        wrapName={wrapName}
      />
      {showSetting && (
        <div className='avatar-name' style={wrapNameStyle}>
          {i18n.t('UserID')}: {user.loginId || user.uniqueId}
        </div>
      )}
    </>
  );
};

type AddUserLabelProps = {
  onClick?: () => void;
};
export const AddUserLabel: React.FC<AddUserLabelProps> = (props) => {
  const { onClick } = props;
  return (
    <div className='add-member-wrapper'>
      <DefaultButton className='add-member-button' onClick={onClick}>
        <img src={plusIcon} alt='Add Member' />
      </DefaultButton>
      <span className='add-member-label'>{i18n.t('AddGroupMembers')}</span>
    </div>
  );
};
